import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { BarChart } from '@mantine/charts';
import {
  IconAddressBook,
  IconAlertTriangle,
  IconMessage,
  IconSpeakerphone,
  IconTrendingUp,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useDashboardRealtime } from '../../lib/realtime';
import { SetupBanner } from '../../components/onboarding/SetupBanner';

type Period = '24h' | '7d' | '30d' | '90d';

/** Axis label for a bucket start: "14:00" for hourly, "30 Aug" for daily. */
function formatBucket(iso: string, unit: string): string {
  const d = new Date(iso);
  return unit === 'hour'
    ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function MetricCard(props: {
  label: string;
  value: number | string;
  hint?: string;
  icon: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          <Text c="dimmed" size="sm">
            {props.label}
          </Text>
          {props.loading ? (
            <Skeleton height={28} width={80} />
          ) : (
            <Text fw={700} size="xl">
              {typeof props.value === 'number' ? props.value.toLocaleString() : props.value}
            </Text>
          )}
          {props.hint && (
            <Text c="dimmed" size="xs">
              {props.hint}
            </Text>
          )}
        </Stack>
        <div style={{ opacity: 0.6 }}>{props.icon}</div>
      </Group>
    </Card>
  );
}

function VendorDashboard() {
  // Realtime: invalidate the four live dashboard queries on every
  // NewMessage / StatusUpdate / etc. so the KPI tiles, timeseries, and
  // top-flows update within a round-trip of the event landing.
  useDashboardRealtime();

  const [period, setPeriod] = useState<Period>('7d');
  // 60 s refetchInterval is a safety net: if a broadcast event misses
  // (browser tab was sleeping, socket dropped, etc.) the dashboard
  // still freshens automatically. Cheap — the four queries are
  // index-driven counts.
  const REFETCH_MS = 60_000;
  const kpis = trpc.analytics.dashboard.kpis.useQuery(undefined, {
    refetchInterval: REFETCH_MS,
  });
  const ts = trpc.analytics.dashboard.whatsappTimeseries.useQuery(
    { period },
    { refetchInterval: REFETCH_MS },
  );
  const topFlows = trpc.analytics.dashboard.botsTopFlows.useQuery(
    { period },
    { refetchInterval: REFETCH_MS },
  );
  const recent = trpc.analytics.dashboard.campaignsRecent.useQuery(undefined, {
    refetchInterval: REFETCH_MS,
  });
  const alerts = trpc.analytics.dashboard.alerts.useQuery();

  // One entry per time bucket (hourly for 24h, daily otherwise). This used to
  // collapse the whole period into a single bar labelled "WhatsApp", which
  // made the period switcher change one bar's height and nothing else.
  const chartData = (ts.data?.points ?? []).map((p) => ({
    bucket: formatBucket(p.bucket, ts.data?.unit ?? 'day'),
    sent: p.sent,
    delivered: p.delivered,
    read: p.read,
    failed: p.failed,
  }));

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap">
          <Title order={2}>Dashboard</Title>
          <SegmentedControl
            value={period}
            onChange={(v) => setPeriod(v as Period)}
            data={[
              { value: '24h', label: '24h' },
              { value: '7d', label: '7d' },
              { value: '30d', label: '30d' },
              { value: '90d', label: '90d' },
            ]}
          />
        </Group>

        {/* Renders only while setup is unfinished and undismissed; above the
            alerts because an unconnected account has nothing else to act on. */}
        <SetupBanner />

        {(alerts.data ?? []).map((a, i) => (
          <Alert
            key={i}
            color={a.severity === 'red' ? 'red' : 'yellow'}
            icon={<IconAlertTriangle />}
          >
            {a.message}
          </Alert>
        ))}

        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <MetricCard
            label="Contacts"
            value={kpis.data?.contactsCount ?? 0}
            icon={<IconAddressBook size={20} />}
            loading={kpis.isPending}
          />
          <MetricCard
            label="Messages (24h)"
            value={kpis.data?.msg24h ?? 0}
            icon={<IconMessage size={20} />}
            loading={kpis.isPending}
          />
          <MetricCard
            label="Messages (7d)"
            value={kpis.data?.msg7d ?? 0}
            hint={kpis.data?.snapshotDate ? 'from rollup' : 'live count'}
            icon={<IconMessage size={20} />}
            loading={kpis.isPending}
          />
          <MetricCard
            label="Active campaigns"
            value={kpis.data?.activeCampaigns ?? 0}
            icon={<IconSpeakerphone size={20} />}
            loading={kpis.isPending}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <Card>
            <Stack gap="xs">
              <Title order={5}>WhatsApp delivery — {period}</Title>
              {ts.isPending ? (
                <Skeleton height={220} />
              ) : ts.isError ? (
                <Alert color="red" icon={<IconAlertTriangle />}>
                  Couldn&apos;t load delivery data. {ts.error.message}
                </Alert>
              ) : chartData.length === 0 ? (
                <Text c="dimmed" size="sm">
                  No messages in this period.
                </Text>
              ) : (
                // Grouped, not stacked: the stages overlap by design — every
                // read message was also delivered and sent — so stacking them
                // would triple-count the same message.
                <BarChart
                  h={220}
                  data={chartData}
                  dataKey="bucket"
                  series={[
                    { name: 'sent', color: 'blue.5' },
                    { name: 'delivered', color: 'teal.5' },
                    { name: 'read', color: 'green.5' },
                    { name: 'failed', color: 'red.5' },
                  ]}
                  withLegend
                />
              )}
            </Stack>
          </Card>

          <Card>
            <Stack gap="xs">
              <Title order={5}>Top bot flows — {period}</Title>
              {topFlows.isPending ? (
                <Skeleton height={120} />
              ) : (topFlows.data ?? []).length === 0 ? (
                <Text c="dimmed" size="sm">
                  No flow matches in this window yet.
                </Text>
              ) : (
                <Stack gap="xs">
                  {(topFlows.data ?? []).map((f) => (
                    <Group key={f.flowId ?? f.name} justify="space-between">
                      <Text size="sm">{f.name}</Text>
                      <Badge variant="light" leftSection={<IconTrendingUp size={12} />}>
                        {f.count}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        </SimpleGrid>

        <Card>
          <Stack gap="xs">
            <Group justify="space-between">
              <Title order={5}>Recent campaigns</Title>
              <Link to="/vendor/campaigns">
                <Text size="sm" c="blue">
                  View all
                </Text>
              </Link>
            </Group>
            {recent.isPending ? (
              <Skeleton height={120} />
            ) : (recent.data ?? []).length === 0 ? (
              <Text c="dimmed" size="sm">
                You haven't sent any campaigns yet.
              </Text>
            ) : (
              <Stack gap="xs">
                {(recent.data ?? []).map((c) => (
                  <Group key={c.id} justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>
                        {c.title}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </Text>
                    </div>
                    <Group gap="xs">
                      <Badge variant="light" color="blue">
                        sent {c.sent}
                      </Badge>
                      <Badge variant="light" color="green">
                        read {c.read}
                      </Badge>
                      <Badge variant="light" color="red">
                        fail {c.failed}
                      </Badge>
                    </Group>
                  </Group>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/')({ component: VendorDashboard });
